import {
  FlexColumn,
  FlexRow,
  LabeledData,
  MaterialIcon,
  useModal,
  useMoment,
  VerticalDivider,
} from '@gorila-shared-ui/components';
import { Button } from 'baseui/button';
import { StatefulPopover } from 'baseui/popover';
import { LabelMedium, ParagraphSmall } from 'baseui/typography';
import { Marker } from 'react-map-gl/mapbox';
import { generatePath } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import {
  ASSET_INFO_ACTION,
  FOLLOW_ACTION,
  HISTORIC_REPORT_ACTION,
  SECONDARIES_ACTION,
  SECONDARIES_PAGE,
  SEND_COMMAND_ACTION,
  TRACKING_PAGE,
} from '../../../constants/app';
import { ASSET_TYPE_PHONE, ASSET_TYPE_VEHICLE } from '../../../constants/asset';
import { useAnimatedLocation } from '../../../hooks/useAnimatedLocation';
import { useCamera } from '../../../hooks/useCamera';
import { useStyles } from '../../../hooks/useStyles';
import { apiParamsState, availableActionsState, isPublicPageState } from '../../../storage/app';
import { CurrentState } from '../../../types/currentState';
import { getAssetTitle } from '../../../utils/assets';
import CommandModal from '../../commands/modal/CommandModal';
import CurrentStateModal from '../../currentState/modal/CurrentStateModal';
import HistoricReportModal from '../../reports/modal/HistoricReportModal';
import VehicleIcon from './VehicleIcon';

type Props = { currentState: CurrentState };

function CurrentStateSharedMarker({ currentState }: Readonly<Props>) {
  const { theme, css } = useStyles();
  const apiParams = useRecoilValue(apiParamsState);
  const actions = useRecoilValue(availableActionsState);
  const isPublicPage = useRecoilValue(isPublicPageState);
  const { zoomTo } = useCamera();
  const { formatDateFromTs } = useMoment();
  const {
    openModal: openCurrentStateModal,
    closeModal: closeCurrentStateModal,
    showModal: showCurrentStateModal,
  } = useModal();
  const { openModal: openCommandModal, closeModal: closeCommandModal, showModal: showCommandModal } = useModal();
  const {
    openModal: openHistoricReportModal,
    closeModal: closeHistoricReportModal,
    showModal: showHistoricReportModal,
  } = useModal();
  const vehicle = currentState.asset.vehicle;
  const assetType = currentState.asset.assetType;
  const targetLng = currentState.point.coordinates[0];
  const targetLat = currentState.point.coordinates[1];
  const { lat, lng } = useAnimatedLocation(targetLat, targetLng);
  const iconWrapper = css({ display: 'inline-block', lineHeight: 0 });
  const canRotate = assetType === ASSET_TYPE_VEHICLE;
  return (
    <>
      <Marker
        longitude={lng}
        latitude={lat}
        rotation={canRotate ? currentState.position_direction || 0 : 0}
      >
        <StatefulPopover
          showArrow
          autoFocus
          returnFocus
          placement="top"
          triggerType="hover"
          popoverMargin={-10}
          content={({ close }) => (
            <FlexColumn
              gap={theme.sizing.scale300}
              classNames={css({
                paddingRight: theme.sizing.scale600,
                paddingLeft: theme.sizing.scale600,
                paddingTop: theme.sizing.scale500,
                paddingBottom: theme.sizing.scale500,
              })}
            >
              <FlexRow gap={theme.sizing.scale200}>
                <LabelMedium>{getAssetTitle(currentState.asset)}</LabelMedium>
                <ParagraphSmall
                  margin={0}
                  color={theme.colors.contentSecondary}
                >
                  ({currentState.asset.label})
                </ParagraphSmall>
              </FlexRow>

              <FlexColumn gap={theme.sizing.scale0}>
                <LabeledData
                  label={'Dirección:'}
                  content={currentState.geoReference}
                />
                <LabeledData
                  label={'Última posición:'}
                  content={`${formatDateFromTs(+currentState.timestamp)}`}
                />
              </FlexColumn>
              <FlexRow
                classNames={css({
                  alignItems: 'center',
                  justifyContent: 'space-evenly',
                })}
              >
                {actions.includes(FOLLOW_ACTION) && (
                  <>
                    <Button
                      shape="circle"
                      kind="tertiary"
                      size="compact"
                      onClick={() =>
                        apiParams &&
                        window.open(
                          generatePath(TRACKING_PAGE.route, {
                            id: apiParams.subClientId,
                            assetId: currentState.asset._id,
                          }),
                          '_blank',
                          'directories=no,titlebar=no,toolbar=no,location=no,status=no,menubar=no,scrollbars=no,resizable=no, height=700px, width=1400px',
                        )
                      }
                    >
                      <MaterialIcon
                        name="open_in_new"
                        size="compact"
                      />
                    </Button>
                    <VerticalDivider />
                  </>
                )}
                {actions.includes(SECONDARIES_ACTION) && !!currentState.asset.secondaries?.length && (
                  <>
                    <Button
                      shape="circle"
                      kind="tertiary"
                      size="compact"
                      onClick={() =>
                        apiParams &&
                        window.open(
                          generatePath(SECONDARIES_PAGE.route, {
                            id: apiParams.subClientId,
                            assetId: currentState.asset._id,
                          }),
                          '_blank',
                          'directories=no,titlebar=no,toolbar=no,location=no,status=no,menubar=no,scrollbars=no,resizable=no, height=700px, width=1400px',
                        )
                      }
                    >
                      <MaterialIcon name="devices_other" />
                    </Button>
                    <VerticalDivider />
                  </>
                )}
                {(actions.includes(ASSET_INFO_ACTION) || isPublicPage) && (
                  <>
                    <Button
                      shape="circle"
                      kind="tertiary"
                      size="compact"
                      onClick={openCurrentStateModal}
                    >
                      <MaterialIcon
                        name="info"
                        size="compact"
                      />
                    </Button>
                    <VerticalDivider />
                  </>
                )}
                {actions.includes(HISTORIC_REPORT_ACTION) && (
                  <>
                    <Button
                      shape="circle"
                      kind="tertiary"
                      size="compact"
                      onClick={openHistoricReportModal}
                    >
                      <MaterialIcon
                        name="timeline"
                        size="compact"
                      />
                    </Button>
                    <VerticalDivider />
                  </>
                )}
                {actions.includes(SEND_COMMAND_ACTION) && assetType !== ASSET_TYPE_PHONE && (
                  <>
                    <Button
                      shape="circle"
                      kind="tertiary"
                      size="compact"
                      onClick={openCommandModal}
                    >
                      <MaterialIcon
                        name="settings_remote"
                        size="compact"
                      />
                    </Button>
                    <VerticalDivider />
                  </>
                )}
                <Button
                  shape="circle"
                  kind="tertiary"
                  size="compact"
                  onClick={() => {
                    close();
                    zoomTo({
                      lat: targetLat, // Zoom targets the final destination
                      lng: targetLng,
                    });
                  }}
                >
                  <MaterialIcon
                    name="zoom_in_map"
                    size="compact"
                  />
                </Button>
              </FlexRow>
            </FlexColumn>
          )}
          overrides={{
            Inner: {
              style: {
                backgroundColor: theme.colors.backgroundPrimary,
              },
            },
            Arrow: {
              style: {
                backgroundColor: theme.colors.backgroundPrimary,
              },
            },
          }}
        >
          <span className={iconWrapper}>
            <VehicleIcon
              color={vehicle?.color}
              assetType={assetType}
              vehicleType={vehicle?.vehicleType}
            />
          </span>
        </StatefulPopover>
      </Marker>
      <CurrentStateModal
        onClose={closeCurrentStateModal}
        isOpen={showCurrentStateModal}
        ident={currentState.ident}
      />
      <CommandModal
        onClose={closeCommandModal}
        isOpen={showCommandModal}
        asset={currentState.asset}
      />
      <HistoricReportModal
        onClose={closeHistoricReportModal}
        isOpen={showHistoricReportModal}
        assetId={currentState.asset._id}
      />
    </>
  );
}

export default CurrentStateSharedMarker;
